import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import Modal from '../../components/Modal';
import { SearchableSelect } from '../../components/workspace/filters/FilterDropdowns';
import { formatDate } from '../../lib/format';
import '../../styles/workspace.css';
import '../../styles/Performance.css';

const PERIODICITY_LABELS = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half Yearly',
  yearly: 'Yearly',
};

const STATUS_COLORS = {
  planned: 'status-pending',
  active: 'status-approved',
  completed: 'status-rejected',
};

const EMPTY_FORM = {
  cycle_name: '',
  year: new Date().getFullYear(),
  periodicity: 'yearly',
  start_date: '',
  end_date: '',
  final_approver_role_id: null,
};

const PAGE_SIZE = 10;

export default function PerformanceReviewCycles() {
  const { appId } = useParams();
  const api = useApi();
  const { canWrite } = useWorkspace();
  const [cycles, setCycles] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(null); // cycleId being started
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    Promise.all([loadCycles(), loadRoles()]);
  }, [appId]);

  const loadCycles = async () => {
    try {
      const data = await api.listReviewCycles(appId);
      setCycles(data.items || []);
    } catch {
      setError('Failed to load review cycles');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await api.roles.list(appId);
      setRoles(data.items || []);
    } catch {
      // silent
    }
  };

  const openCreate = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setError('');
    setShowForm(true);
  };

  const openEdit = cycle => {
    setFormData({
      cycle_name: cycle.cycle_name,
      year: cycle.year,
      periodicity: cycle.periodicity,
      start_date: cycle.start_date,
      end_date: cycle.end_date,
      final_approver_role_id: cycle.final_approver_role_id || null,
    });
    setEditingId(cycle.id);
    setError('');
    setShowForm(true);
  };

  const handleStartReviews = async cycle => {
    if (!window.confirm(`Start reviews for "${cycle.cycle_name}"? This will create a review record for every user who joined on or before the cycle end date, and mark the cycle as Active.`)) return;
    setStarting(cycle.id);
    setError('');
    try {
      const result = await api.startReviews(appId, cycle.id);
      alert(`Done! Created ${result.created} new review(s). ${result.skipped} already existed.`
        + (result.not_yet_joined ? ` ${result.not_yet_joined} skipped (joined after the cycle ended).` : ''));
      loadCycles();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start reviews');
    } finally {
      setStarting(null);
    }
  };

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!formData.cycle_name.trim()) { setError('Cycle name is required'); return; }
    if (!formData.start_date)        { setError('Start date is required'); return; }
    if (!formData.end_date)          { setError('End date is required'); return; }
    if (formData.start_date >= formData.end_date) { setError('End date must be after start date'); return; }

    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await api.updateReviewCycle(appId, editingId, formData);
      } else {
        await api.createReviewCycle(appId, formData);
      }
      setShowForm(false);
      loadCycles();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save cycle');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading review cycles…</div>;

  return (
    <div className="entity-page">
      <div className="page-header">
        <h1 className="page-title">Performance Review Cycles</h1>
        {canWrite && (
          <button className="btn-primary" onClick={openCreate}>
            + Create Cycle
          </button>
        )}
      </div>

      {error && !showForm && <div className="error-message">{error}</div>}

      {cycles.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📅</div>
          <p style={{ fontSize: 16, margin: 0 }}>No review cycles yet.</p>
          {canWrite && <p style={{ fontSize: 14, marginTop: 8 }}>Create one to get started with performance reviews.</p>}
        </div>
      ) : (() => {
        const totalPages = Math.max(1, Math.ceil(cycles.length / PAGE_SIZE));
        const pagedCycles = cycles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
        return (
          <div className="entity-table-wrap" style={{ display: 'flex', flexDirection: 'column' }}>
            {totalPages > 1 && (
              <div className="tl-pagination tl-pagination--top">
                <span className="tl-page-info">
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, cycles.length)} of {cycles.length} cycles
                </span>
                <div className="tl-page-btns">
                  <button className="tl-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                    .reduce((acc, n, i, arr) => {
                      if (i > 0 && n - arr[i - 1] > 1) acc.push('…');
                      acc.push(n);
                      return acc;
                    }, [])
                    .map((n, i) =>
                      n === '…'
                        ? <span key={`ellipsis-${i}`} className="tl-page-ellipsis">…</span>
                        : <button key={n} className={`tl-page-num${n === page ? ' active' : ''}`} onClick={() => setPage(n)}>{n}</button>
                    )
                  }
                  <button className="tl-page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
                </div>
              </div>
            )}
            <div style={{ height: 'calc(100vh - 220px)', overflowY: 'auto' }}>
              <table className="entity-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 160 }}>Cycle Name</th>
                    <th>Year</th>
                    <th>Periodicity</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Status</th>
                    {canWrite && <th style={{ width: 80 }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {pagedCycles.map(cycle => (
                    <tr key={cycle.id}>
                      <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}><strong>{cycle.cycle_name}</strong></td>
                      <td>{cycle.year}</td>
                      <td>{PERIODICITY_LABELS[cycle.periodicity] || cycle.periodicity}</td>
                      <td>{formatDate(cycle.start_date)}</td>
                      <td>{formatDate(cycle.end_date)}</td>
                      <td>
                        <span className={`status-badge ${STATUS_COLORS[cycle.status] || ''}`}>
                          {cycle.status || 'planned'}
                        </span>
                      </td>
                      {canWrite && (
                        <td className="row-actions" style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn-link-sm" onClick={() => openEdit(cycle)}>Edit</button>
                          {cycle.status !== 'completed' && (
                            <button
                              className="btn-link-sm"
                              style={{ color: '#27ae60', marginLeft: 8 }}
                              onClick={() => handleStartReviews(cycle)}
                              disabled={starting === cycle.id}
                            >
                              {starting === cycle.id ? 'Starting…' : '▶ Start Reviews'}
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {showForm && (
        <Modal open title={editingId ? 'Edit Review Cycle' : 'New Review Cycle'} onClose={() => setShowForm(false)}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Cycle Name */}
            <div>
              <label style={labelStyle}>Cycle Name *</label>
              <input
                className="form-input"
                type="text"
                value={formData.cycle_name}
                onChange={e => set('cycle_name', e.target.value)}
                placeholder="e.g. Annual Review 2025"
                maxLength={100}
                autoFocus
                style={inputStyle}
              />
            </div>

            {/* Year + Periodicity side-by-side */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={labelStyle}>Year *</label>
                <input
                  className="form-input"
                  type="number"
                  min="2020"
                  max="2040"
                  value={formData.year}
                  onChange={e => set('year', parseInt(e.target.value))}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Periodicity *</label>
                <SearchableSelect
                  btnClass="form-select-btn"
                  matchTriggerWidth
                  showAll={false}
                  value={formData.periodicity}
                  onChange={val => set('periodicity', val)}
                  options={[
                    { value: 'monthly', label: 'Monthly' },
                    { value: 'quarterly', label: 'Quarterly' },
                    { value: 'half_yearly', label: 'Half Yearly' },
                    { value: 'yearly', label: 'Yearly' },
                  ]}
                />
              </div>
            </div>

            {/* Start + End Date side-by-side */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={labelStyle}>Start Date *</label>
                <input
                  className="form-input"
                  type="date"
                  value={formData.start_date}
                  onChange={e => set('start_date', e.target.value)}
                  min={`${formData.year}-01-01`}
                  max={`${formData.year + 1}-12-31`}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>End Date *</label>
                <input
                  className="form-input"
                  type="date"
                  value={formData.end_date}
                  onChange={e => set('end_date', e.target.value)}
                  min={formData.start_date || `${formData.year}-01-01`}
                  max={`${formData.year + 1}-12-31`}
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Final Approver Role */}
            <div>
              <label style={labelStyle}>Final Approver Role <span style={{ color: '#999', fontWeight: 400 }}>(optional)</span></label>
              <SearchableSelect
                placeholder="— None (admin decides) —"
                btnClass="form-select-btn"
                matchTriggerWidth
                showAll={false}
                value={formData.final_approver_role_id || ''}
                onChange={val => set('final_approver_role_id', val ? parseInt(val) : null)}
                options={roles.map(role => ({ value: String(role.id), label: role.title }))}
              />
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#888' }}>
                Person in this role will do the final review approval.
              </p>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
              <button type="button" onClick={() => setShowForm(false)} style={cancelBtnStyle}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Cycle'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

const labelStyle = {
  display: 'block',
  fontWeight: 600,
  fontSize: 13,
  color: 'var(--text-primary)',
  marginBottom: 6,
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
};

const cancelBtnStyle = {
  padding: '8px 18px',
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--bg-card)',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  fontSize: 14,
};
